import { atom } from "jotai"
import {
	HealingAiInputNormalization,
	HealingItem,
	Rectangle,
	HealingGeneratedSource,
	HealingMode,
	AiVariantPromptStrategy,
} from "@/core/types"
import { getEditor } from "@/utils/useEditor"
import {
	BrushStroke,
	calcBoundingBox,
	getPaddedAndClampedRect,
	getCenterOfStrokes,
} from "@/core/utils/brushing"
import { uuid } from "@/utils/uuid"
import { currentAdjustmentsAtom, saveCurrentAdjustmentState } from "../store-history"
import { vec2 } from "gl-matrix"
import { store } from "../store"
import { rectCenter, rectClamp, rectPadding } from "@/utils/math"
import { showToast } from "@/store/ui-views-state"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer toast i18n
import i18n from "@/i18n"
import { generateQwenImageEdit, QwenApiError } from "@/api/qwen-image-edit"
import { bitmapToImageData, imageDataToBase64 } from "@/core/utils/image-data"
import {
	type CapturedRegion,
	applyOrientation,
	downloadDebugImage,
	rotateBitmap,
	bitmapToBlob,
	blobToBase64,
	base64ToBlob,
	texturePixelsToImageData,
	applyShrinkedRedMaskOverlay,
	applyRedOutlineOverlay,
} from "./healing-utils"
import { AI } from "@/lib/ai/ai"
import { computeAlignmentFromFlowRobust } from "@/lib/ai/alignment/ai-image-align"
import { currentPhotoAtom, currentPhotoIdAtom, photosAtom } from "@/store/photo/photos-state"
import { CatalogManager } from "@/lib/catalog"
import { HistoryRecordAction } from "@/lib/catalog/types"
import { storeAdjustmentsHistoryAndUpdatePhoto } from "../processing/processing-storage"
import { AdjustmentsResult } from "../processing/image-processing"
import { isGenerativeFillEnabled } from "@/utils/measurement/feature-flag"
import { consumeGenAiCredit } from "@/api/payments-api"
import axios from "axios"
import toast from "react-hot-toast"
import { Sentry } from "@/telemetry/sentry"
import { queryClient } from "@/api/constants"
import { GetUserResponseBody } from "@/api/dto/users"
import { canAddAiHealingVariationBatch, stackAiHealingVariations } from "./healing-variation-stack"

export const isHealingHoverAtom = atom<boolean>(false)
export const isHealingPreviewEnabledAtom = atom<boolean>(true)
const selectedHealingIdAtom = atom<string | null>(null)

export type AiModel = "qwen-image-edit-2511" // keep this type in case we need to add more models
export const healingModeAtom = atom<HealingMode>(isGenerativeFillEnabled() ? "ai-erase" : "healing")
export const healingAiPromptAtom = atom<string>("")
export const healingAiPendingAtom = atom<boolean>(false)
/** Incremented each generation; cancel bumps it so in-flight runs see a stale ID and skip applying. */
let healingAiGenerationId = 0
// Derived atom: erasing mode is when healingMode is "ai-erase"
export const healingAiErasingModeAtom = atom((get) => get(healingModeAtom) === "ai-erase")
export const healingAiShowMaskAtom = atom<boolean>(false)
export const healingAiReferenceImageAtom = atom<File[]>([])
export const healingApplyBrushMaskAtom = atom<boolean>(true) // Default: apply brush mask
export const healingApplyAlignmentAtom = atom<boolean>(true) // Default: apply optical flow alignment correction

// Temporary AI item for showing edges (draft, not yet generated)
// This is per-photo and gets cleared on photo switch or when generation starts
export const healingAiTemporaryItemAtom = atom<HealingItem | null>(null)

// Pending AI item that's being generated (app-wide, at most one)
// Stored with photo ID so it only shows on the correct photo
const healingAiPendingItemWithPhotoAtom = atom<{ item: HealingItem; photoId: string } | null>(null)

// Derived atom: shows temporary draft OR pending item (if on correct photo)
export const healingAiVisibleItemAtom = atom((get) => {
	// First check for temporary draft (current photo)
	const tempItem = get(healingAiTemporaryItemAtom)
	if (tempItem) return tempItem

	// Then check for pending item (only show on correct photo)
	const pendingData = get(healingAiPendingItemWithPhotoAtom)
	if (!pendingData) return null

	const currentPhoto = get(currentPhotoAtom)
	if (!currentPhoto || currentPhoto.id !== pendingData.photoId) {
		return null // Hide pending item if on different photo
	}

	return pendingData.item
})

// Derived atom for prompt (allows reading and writing)
export const healingAiPromptDerivedAtom = atom(
	(get) => {
		return get(healingAiPromptAtom)
	},
	(_get, set, newPrompt: string) => {
		set(healingAiPromptAtom, newPrompt)
	},
)

export const selectedHealingAtom = atom(
	(get) => {
		const selectedId = get(selectedHealingIdAtom)
		if (!selectedId) {
			return null
		}

		// Check temporary item first (if it matches the selected ID)
		const temporaryItem = get(healingAiTemporaryItemAtom)
		if (temporaryItem?.id === selectedId) {
			return temporaryItem
		}

		// Look up the actual healing item from adjustments
		const adjustments = get(currentAdjustmentsAtom)
		const healingItem = adjustments.healingMasks?.find((h) => h.id === selectedId)

		if (!healingItem) {
			return null
		}

		return healingItem
	},
	(_, set, healing: HealingItem | null) => {
		set(selectedHealingIdAtom, healing?.id ?? null)
	},
)

export const healingItemsAtom = atom((get) => {
	const adjustments = get(currentAdjustmentsAtom)
	return adjustments.healingMasks ?? []
})

const getHealingAiErrorMessage = (error: unknown): string => {
	if (error instanceof QwenApiError && error.errorCode === "nsfw_blocked") {
		return i18n.t("store:healingState.aiHealingNsfwBlockedError")
	}

	let errorMessage = i18n.t("store:healingState.aiHealingFailedError")

	if (error instanceof Error) {
		const errorText = error.message.toLowerCase()

		if (errorText.includes("api key") || errorText.includes("unauthorized")) {
			errorMessage = i18n.t("store:healingState.aiHealingApiKeyError")
		} else if (errorText.includes("rate limit")) {
			errorMessage = i18n.t("store:healingState.aiHealingRateLimitError")
		} else if (errorText.includes("too large") || errorText.includes("413")) {
			errorMessage = i18n.t("store:healingState.aiHealingImageTooLargeError")
		} else if (errorText.includes("network") || errorText.includes("fetch")) {
			errorMessage = i18n.t("store:healingState.aiHealingNetworkError")
		} else if (errorText.includes("timeout")) {
			errorMessage = i18n.t("store:healingState.aiHealingTimeoutError")
		} else if (errorText.includes("invalid") || errorText.includes("400")) {
			errorMessage = i18n.t("store:healingState.aiHealingInvalidRequestError")
		} else if (errorText.includes("server error") || errorText.includes("500")) {
			errorMessage = i18n.t("store:healingState.aiHealingServiceUnavailableError")
		} else if (errorText.includes("cors")) {
			errorMessage = i18n.t("store:healingState.aiHealingCorsError")
		} else if (errorText.includes("qwen image generation failed for all variations")) {
			errorMessage = i18n.t("store:healingState.aiHealingFailedError")
		} else if (error.message.length > 0 && error.message.length < 150) {
			errorMessage = i18n.t("store:healingState.aiHealingFailedWithDetail", {
				detail: error.message,
			})
		}
	}

	return errorMessage
}

const getHealingAiInputNormalization = (): HealingAiInputNormalization => {
	const photo = getEditor().photo
	const adjustments = photo.adjustments

	return {
		effectiveExposure:
			photo.type === "bitmap" ? 0 : (adjustments.exposure ?? 0) + (adjustments.shadows ?? 0),
	}
}

// Shared utility function for consistent padding calculation across AI healing functions
const calculateAiHealingPadding = (
	strokesRect: Rectangle,
	prompt: string,
	isErasingMode: boolean,
	imgSize: vec2,
) => {
	let contextPadding = 0.1 // 0.25
	const minPadding = 0.04
	if (
		isErasingMode ||
		prompt.toLowerCase().includes("remov") ||
		prompt.toLowerCase().includes("eras") ||
		prompt.toLowerCase().includes("get rid of")
	) {
		// bigger padding for erasing
		contextPadding = 0.3
	}
	const [width, height] = imgSize
	const padPixels =
		Math.max(strokesRect.width * width, strokesRect.height * height) * contextPadding
	const minPaddingPixels = minPadding * Math.min(width, height)
	let padW = Math.max(minPaddingPixels, padPixels)
	let padH = padW
	const additionalPadW = Math.max(0, minPadding * width - padW)
	const additionalPadH = Math.max(0, minPadding * height - padH)

	// Calculate initial padded dimensions
	let paddedW = strokesRect.width * width + 2 * padW
	let paddedH = strokesRect.height * height + 2 * padH

	// pad to square if additional padding is available (e.g. small region)
	if (additionalPadW > 0 && paddedW < paddedH) {
		padW += Math.min(additionalPadW, Math.max(0, (paddedH - paddedW) / 2))
	} else if (additionalPadH > 0 && paddedH < paddedW) {
		padH += Math.min(additionalPadH, Math.max(0, (paddedW - paddedH) / 2))
	}
	paddedW = strokesRect.width * width + 2 * padW
	paddedH = strokesRect.height * height + 2 * padH

	// Determine target aspect ratio based on padded region size
	// increase from lowRatio to highRatio while max(H, W) increases from 0.1 to 0.4
	const paddedSizePixels = Math.max(paddedW, paddedH)
	const imageSizeMax = Math.max(width, height)
	const lowRatio = 1.3333
	const highRatio = 2
	let targetRatio = lowRatio
	if (paddedSizePixels < 0.1 * imageSizeMax) {
		targetRatio = lowRatio
	} else if (paddedSizePixels < 0.4 * imageSizeMax) {
		// Interpolate from lowRatio to highRatio as size grows from 0.1 to 0.4
		const t = (paddedSizePixels - 0.1 * imageSizeMax) / (0.3 * imageSizeMax)
		targetRatio = lowRatio + t * (highRatio - lowRatio) // lowRatio -> highRatio
	} else {
		targetRatio = highRatio
	}

	// Adjust padding to reduce aspect ratio if it's too high (only reduce, never increase)
	// Target: max(paddedH/paddedW, paddedW/paddedH) <= targetRatio
	const currentRatio = Math.max(paddedH / paddedW, paddedW / paddedH)

	if (currentRatio > targetRatio) {
		// Current ratio is too high, add padding to shorter dimension to reduce it
		if (paddedW > paddedH) {
			// Width is longer, increase height padding
			// targetW / targetH = targetRatio → targetH = targetW / targetRatio
			const targetH = paddedW / targetRatio
			padH = (targetH - strokesRect.height * height) / 2
		} else {
			// Height is longer, increase width padding
			// targetH / targetW = targetRatio → targetW = targetH / targetRatio
			const targetW = paddedH / targetRatio
			padW = (targetW - strokesRect.width * width) / 2
		}
	}

	const result = rectPadding(strokesRect, padW / width, padH / height)

	return rectClamp(result, 0.0, 1.0)
}

export const healingCompleteCurrentStroke = (completedStroke?: BrushStroke) => {
	const editor = getEditor()
	const healingMasks = editor.adjustments.healingMasks ?? []
	const currentStroke = completedStroke ?? editor.healingCurrentStroke
	if (healingMasks && currentStroke && currentStroke.points.length > 0) {
		// Don't set hover immediately - wait until we have an actual healing item to hover
		// store.set(isHealingHoverAtom, true)

		const strokeCopy: BrushStroke[] = [
			{ ...currentStroke, points: currentStroke.points.slice() },
		]

		const strokesRect = calcBoundingBox(
			strokeCopy,
			editor.photo.texture.height / editor.photo.texture.width,
		)

		const destinationRect = getPaddedAndClampedRect(strokesRect)
		const strokeCenter = getCenterOfStrokes(strokeCopy, rectCenter(destinationRect))

		const healingMode = store.get(healingModeAtom)
		if (healingMode !== "healing") {
			store.set(selectedHealingAtom, null)
			store.set(healingAiPendingAtom, false)

			// Create a temporary healing item for showing edges only (not added to healingMasks)
			// This won't trigger healing computation, just UI edge rendering
			const applyBrushMask = store.get(healingApplyBrushMaskAtom)
			const applyAlignment = store.get(healingApplyAlignmentAtom)

			const center =
				strokeCenter ??
				vec2.fromValues(
					destinationRect.x + destinationRect.width / 2,
					destinationRect.y + destinationRect.height / 2,
				)

			const temporaryHealingItem: HealingItem = {
				id: uuid(),
				strokes: strokeCopy,
				strokesRect: strokesRect,
				destinationRect: destinationRect,
				sourceRect: destinationRect, // Same as destination for temp item
				sourceCenter: center,
				destinationCenter: center,
				applyBrushMask: applyBrushMask,
				applyAlignment: applyAlignment,
				aiVariations: [], // empty array marks it as temporary/pending
				mode: healingMode,
			}

			store.set(healingAiTemporaryItemAtom, temporaryHealingItem)

			Sentry.addBreadcrumb({
				category: "healing-ai.outline",
				level: "info",
				message: "temp-item-set", // i18n-ignore — Sentry breadcrumb identifier
				data: { itemId: temporaryHealingItem.id },
			})

			// Select the temporary item
			// Don't set hover - edges are always visible, and hover will activate naturally when user moves over them
			store.set(selectedHealingAtom, temporaryHealingItem)

			// Clear the current stroke
			editor.healingCurrentStroke = null
			editor.render({ reason: "healing-ai-temp-item", isDownsampled: true })
			return
		}

		// avoid adding red mask to history preview
		editor.healingCurrentStroke = null

		void editor.renderer.healingSourceDetector
			.detect(destinationRect, strokeCopy)
			.then((sourceRect) => {
				if (sourceRect) {
					const destinationCenter =
						strokeCenter ??
						vec2.fromValues(
							destinationRect.x + destinationRect.width / 2,
							destinationRect.y + destinationRect.height / 2,
						)

					const sourceCenter = vec2.fromValues(
						destinationCenter[0] + sourceRect.x - destinationRect.x,
						destinationCenter[1] + sourceRect.y - destinationRect.y,
					)

					const healingItem = {
						id: uuid(),
						strokes: strokeCopy,
						strokesRect,
						sourceRect,
						destinationRect,
						destinationCenter,
						sourceCenter,
						mode: healingMode,
					}
					const newHealings = (healingMasks ?? []).slice()
					newHealings.push(healingItem)
					editor.setAdjustments({ healingMasks: newHealings })
					void saveCurrentAdjustmentState({
						message: `Remove`,
						changedValue: "Heal",
					})

					// Select the new healing item directly (no lens needed)
					store.set(selectedHealingAtom, healingItem)
					store.set(isHealingHoverAtom, true) // Now we can hover over the completed item
				}
			})
	}
}

const createHealingWithAiCore = async (options: {
	strokeCopy: BrushStroke[]
	strokesRect: Rectangle
	destinationRect: Rectangle
	strokeCenter: vec2 | null
	replaceHealingId?: string
	replaceFullRect?: Rectangle
}): Promise<{
	results: AiHealingResult[]
	prompt: string
	enhancedPrompt: string | null
	paddedRect: Rectangle
}> => {
	const { strokeCopy, strokesRect, destinationRect, replaceHealingId, replaceFullRect } = options
	const editor = getEditor()

	const prompt = store.get(healingAiPromptDerivedAtom)?.trim() ?? ""
	const isErasingMode = store.get(healingAiErasingModeAtom)

	// Use shared padding calculation for consistency
	const imgSize: vec2 = [
		editor.photo.imageState.originalImageSize[0],
		editor.photo.imageState.originalImageSize[1],
	]
	// paddedRect depends on prompt, so for re-generation, we use the original full rect to ensure the same padding
	const paddedRect =
		replaceFullRect ?? calculateAiHealingPadding(strokesRect, prompt, isErasingMode, imgSize)
	store.set(healingAiPendingAtom, true)

	try {
		const aiInputNormalization = getHealingAiInputNormalization()

		const capture = captureHealingRegion(
			editor,
			paddedRect,
			destinationRect,
			aiInputNormalization,
			replaceHealingId,
		)
		if (!capture) {
			throw new Error("Failed to capture healing region")
		}

		const showMask = store.get(healingAiShowMaskAtom)

		const cleanBase64 = capture.base64
		let redMaskBase64: string | undefined
		let outlinedBase64: string | undefined

		// Get reference images from atom if available
		const referenceImageFiles = store.get(healingAiReferenceImageAtom)

		// Erase mode: server now writes its own prompt from the outlined region.
		// Send the clean patch as `images` and the red-outlined patch as `outlined_image`.
		if (isErasingMode) {
			outlinedBase64 = applyRedOutlineOverlay(capture, strokeCopy, paddedRect)
		}

		// Apply red mask overlay for when showMask is enabled (generate-mode debug feature)
		let maskInstruction = ""
		if (showMask && !isErasingMode) {
			redMaskBase64 = applyShrinkedRedMaskOverlay(capture, strokeCopy, paddedRect)
			maskInstruction = "Edit inside the red mask, and remove the red overlay: "
		}

		// Convert reference image files to base64 (generate mode only)
		const base64ReferenceImages: Array<{ data: string; mimeType: string }> = []
		if (!isErasingMode && referenceImageFiles.length > 0) {
			for (const referenceImageFile of referenceImageFiles) {
				try {
					const reader = new FileReader()
					const base64Result = await new Promise<string>((resolve, reject) => {
						reader.onload = () => {
							const result = reader.result
							if (typeof result !== "string") {
								reject(new Error("Failed to read file as string"))
								return
							}
							const base64Data = result.split(",")[1]
							if (!base64Data) {
								reject(new Error("Invalid data URL format"))
								return
							}
							resolve(base64Data)
						}
						reader.onerror = () => reject(new Error("Failed to read file"))
						reader.readAsDataURL(referenceImageFile)
					})
					base64ReferenceImages.push({
						data: base64Result,
						mimeType: referenceImageFile.type,
					})
				} catch (error) {
					console.error("Failed to convert reference image to base64:", error)
				}
			}
		}

		console.log(`Using AI model: Qwen (mode=${isErasingMode ? "erase" : "generate"})`)
		const aiResult = await generateQwenImageEdit({
			mode: isErasingMode ? "erase" : "generate",
			// Erase mode: server ignores the prompt and synthesizes one from the outline.
			prompt: isErasingMode ? undefined : maskInstruction + prompt,
			base64Image: isErasingMode ? cleanBase64 : (redMaskBase64 ?? cleanBase64),
			base64OutlinedImage: outlinedBase64,
			base64ReferenceImages,
			mimeType: "image/jpeg", // JPEG 0.99 - nearly lossless
		})

		// Process all variations from the AI result
		const results: AiHealingResult[] = []

		for (const variation of aiResult.variations) {
			const aiBlob = base64ToBlob(variation.data, variation.mimeType ?? "image/png")
			const result = await processAiResult({
				aiBlob,
				capture,
			})
			results.push({
				...result,
				enhancedPrompt: variation.enhancedPrompt,
				promptStrategy: variation.promptStrategy,
			})
		}

		console.log(`Generated ${results.length} AI healing variations`)

		// `prompt` doubles as "what the textarea shows when re-selecting this variation"
		// and is also the input we'd resend on regeneration — so for generate mode we
		// always store the user's original input (avoids enhancement-on-enhancement drift).
		// For erase mode the user has no editable input; keep the server prompt debug-only.
		// `enhancedPrompt` is the server's actual diffusion input — debug-only display.
		const displayPrompt = isErasingMode ? "" : prompt

		return {
			results,
			prompt: displayPrompt,
			enhancedPrompt: aiResult.enhancedPrompt,
			paddedRect,
		}
	} catch (error) {
		console.error("Failed to generate AI healing", error)
		store.set(isHealingHoverAtom, false)
		throw error // Re-throw the error so callers can handle it
	} finally {
		store.set(healingAiPendingAtom, false)
	}
}

type AiHealingResult = {
	fullBitmap: ImageBitmap
	fullBase64: string
	cropRect: Rectangle
	alignment: { dx: number; dy: number; scale?: number; confidence: number }
	orientation: number
	enhancedPrompt: string | null
	promptStrategy?: AiVariantPromptStrategy
	aiInputNormalization: HealingAiInputNormalization
}

/**
 * Apply AI healing result to photo (current or background)
 * Handles both new generation and regeneration cases
 */
const applyAiHealingResult = async (params: {
	targetPhotoId: string
	baseHealingItem: HealingItem
	aiResults: {
		results: AiHealingResult[]
		prompt: string
		enhancedPrompt: string | null
		paddedRect: Rectangle
	}
	isRegenerate: boolean
	healingMasks: HealingItem[]
}) => {
	const { targetPhotoId, baseHealingItem, aiResults, isRegenerate, healingMasks } = params
	const { results, prompt, enhancedPrompt, paddedRect } = aiResults
	const editor = getEditor()

	// Log the size of AI generated data
	const totalSizeKB = results.reduce((sum, r) => sum + Math.round(r.fullBase64.length / 1024), 0)
	const logPrefix = isRegenerate ? "Regenerating" : "Storing"
	console.log(
		`${logPrefix} ${results.length} AI healing variations: ${
			results[0]?.fullBitmap.width || 0
		}x${results[0]?.fullBitmap.height || 0}, ${totalSizeKB}KB total (JPEG)`,
	)

	const newAiVariations: HealingGeneratedSource[] = results.map((r) => {
		const variationEnhancedPrompt =
			baseHealingItem.mode === "ai-erase"
				? r.enhancedPrompt
				: r.enhancedPrompt && r.enhancedPrompt !== prompt
					? r.enhancedPrompt
					: enhancedPrompt && enhancedPrompt !== prompt
						? enhancedPrompt
						: null

		return {
			mimeType: "image/jpeg",
			data: r.fullBase64,
			width: r.fullBitmap.width,
			height: r.fullBitmap.height,
			cropRect: r.cropRect,
			fullRect: paddedRect,
			prompt: prompt,
			// For generate mode, only persist enhancedPrompt when it actually differs from the user input —
			// keeps the field empty (and the debug textarea hidden) until the server
			// starts rewriting prompts in generate mode.
			...(variationEnhancedPrompt ? { enhancedPrompt: variationEnhancedPrompt } : {}),
			aiInputNormalization: r.aiInputNormalization,
			alignment: r.alignment,
			orientation: r.orientation,
			aiModel: "qwen-image-edit-2511",
			promptStrategy: r.promptStrategy,
		}
	})
	const { aiVariations, selectedVariation } = stackAiHealingVariations(
		isRegenerate ? baseHealingItem.aiVariations : undefined,
		newAiVariations,
	)

	// Create new healing item with AI variations
	const newHealingItem: HealingItem = {
		...baseHealingItem,
		id: uuid(), // always generate a new id for new healing item to trigger re-render (different key in assets resolver)
		aiVariations,
		selectedVariation,
	}

	// Prepare new healings array
	const newHealings = isRegenerate
		? healingMasks.filter((item) => item.id !== baseHealingItem.id)
		: healingMasks.slice()
	newHealings.push(newHealingItem)

	// Determine action message
	const message = newHealingItem.mode === "ai-erase" ? "AI Erase" : "AI Generate"
	const actionLabel = newHealingItem.mode === "ai-erase" ? "Remove" : "Generate"
	const historyMessage = message

	// Check if user switched to a different photo during AI generation
	const currentPhotoId = store.get(currentPhotoIdAtom)

	if (currentPhotoId === targetPhotoId) {
		// Apply to current photo - normal flow with render and selection
		editor.setAdjustments({ healingMasks: newHealings })
		saveCurrentAdjustmentState({
			message: historyMessage,
			changedValue: actionLabel,
		})

		// Select the new healing item
		store.set(selectedHealingAtom, newHealingItem)
		if (!isRegenerate) {
			store.set(isHealingHoverAtom, true) // Enable hover for new items
		}

		console.log(
			`✅ AI healing ${isRegenerate ? "regenerated and " : ""}applied to current photo`,
		)
	} else {
		// User switched photos - save to the correct photo without rendering
		const photos = store.get(photosAtom)
		const targetPhoto = photos[targetPhotoId]
		if (targetPhoto) {
			// Fetch current adjustments for the target photo
			const targetAdjustments = await CatalogManager.fetchAdjustments(targetPhoto)

			// Update with new healing masks
			const updatedAdjustments = {
				...targetAdjustments,
				healingMasks: newHealings,
			}

			const result: AdjustmentsResult = {
				adjustments: updatedAdjustments,
				preview: null,
				imageInfo: null,
				originalPreview: null,
			}
			await storeAdjustmentsHistoryAndUpdatePhoto(targetPhoto, result, {
				message: historyMessage,
				changedValue: actionLabel,
				action: HistoryRecordAction.ManuallyAdjust,
			})

			const message = newHealingItem.mode === "ai-erase" ? "AI Erase" : "AI Generate"
			showToast(i18n.t("store:healingState.aiHealingSavedToPrevious", { message }))
			console.log(
				`✅ ${message} ${
					isRegenerate ? "regenerated and " : "generated and "
				}saved to background photo (${targetPhotoId})`,
			)
		} else {
			console.warn(`⚠️ Target photo not found, ${message} result discarded`)
			showToast(i18n.t("store:healingState.aiHealingResultDiscarded", { message }))
		}
	}

	// Clear temporary item after successful generation (only for new, not regenerate)
	if (!isRegenerate) {
		store.set(healingAiTemporaryItemAtom, null)
	}
}

const generateAiHealingResult = async (
	healingItem: HealingItem,
	isRegenerate: boolean,
): Promise<
	| {
			targetPhotoId: string
			healingMasks: HealingItem[]
			aiResults: {
				results: AiHealingResult[]
				prompt: string
				enhancedPrompt: string | null
				paddedRect: Rectangle
			}
	  }
	| undefined
> => {
	const editor = getEditor()
	const healingMasks = editor.adjustments.healingMasks || []

	// Capture the target photo ID before async operation
	const targetPhotoId = editor.photo.id
	const selectedAiSource = healingItem.aiVariations?.[healingItem.selectedVariation ?? 0]

	// Prepare options for AI generation
	const options = {
		strokeCopy: healingItem.strokes,
		strokesRect: healingItem.strokesRect,
		destinationRect: healingItem.destinationRect,
		strokeCenter: healingItem.destinationCenter,
		...(isRegenerate && {
			replaceHealingId: healingItem.id,
			replaceFullRect: selectedAiSource?.fullRect,
		}),
	}

	// Generate AI result
	const aiResults = await createHealingWithAiCore(options)

	return {
		targetPhotoId,
		aiResults,
		healingMasks,
	}
}

const consumeGenAiCreditAndCancelIfNeeded = async (): Promise<boolean> => {
	const idempotencyKey = uuid()
	try {
		await consumeGenAiCredit(idempotencyKey).then(() => {
			void queryClient.invalidateQueries({ queryKey: ["user"] })
		})

		return true
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error.code === "ERR_NETWORK") {
				toast.error(i18n.t("store:healingState.aiHealingNetworkInterruptedError"))
			} else if (error.response?.status === 400 && error.response?.data.detail.reason) {
				toast.error(error.response?.data.detail.reason)
			} else {
				toast.error(i18n.t("store:healingState.aiHealingGenericError"))
			}
		} else {
			toast.error(i18n.t("store:healingState.aiHealingGenericError"))
		}

		Sentry.captureException(error)

		return false
	}
}

const hasEnoughGenAiCredits = () => {
	const user = queryClient.getQueryData<GetUserResponseBody>(["user"])
	const remainingCredits = user?.totalGenAiCreditsAvailable

	// when credits are unknown, allow generation to proceed.
	// This avoids false "out of credits" UX while user data is still loading or temporarily unavailable.
	// Backend credit consumption remains the source of truth and will reject real out-of-credit cases.
	if (typeof remainingCredits !== "number") {
		return true
	}

	if (remainingCredits <= 0) {
		toast.error(i18n.t("store:healingState.aiHealingOutOfCredits"))
		return false
	}
	return true
}

export const generateAiHealing = async () => {
	if (store.get(healingAiPendingAtom)) {
		return
	}

	if (!hasEnoughGenAiCredits()) {
		return
	}

	const selectedHealing = store.get(selectedHealingAtom)
	const temporaryItem = store.get(healingAiTemporaryItemAtom)

	// Determine which healing item to process and whether it's a regeneration
	const healingItem = selectedHealing?.aiVariations ? selectedHealing : temporaryItem
	const isRegenerate = !!selectedHealing?.aiVariations?.length

	if (!healingItem) {
		return
	}

	if (
		isRegenerate &&
		!canAddAiHealingVariationBatch(selectedHealing?.aiVariations?.length ?? 0)
	) {
		toast.error(i18n.t("store:healingState.aiHealingVariationLimit"))
		return
	}

	// Clear current stroke for new generation
	if (!isRegenerate) {
		getEditor().healingCurrentStroke = null
	}

	const runId = ++healingAiGenerationId

	store.set(healingAiPendingAtom, true)

	try {
		// Move temporary item to pending (so user can draw new masks on other photos)
		if (temporaryItem && !isRegenerate) {
			const currentPhoto = store.get(currentPhotoAtom)
			if (currentPhoto) {
				store.set(healingAiPendingItemWithPhotoAtom, {
					item: temporaryItem,
					photoId: currentPhoto.id,
				})
				store.set(healingAiTemporaryItemAtom, null) // Clear temp so new drafts can be created
			}
		}

		// Set current applyBrushMask and applyAlignment from atoms before processing in case the temporary item is used and not updated to UI
		healingItem.applyAlignment = store.get(healingApplyAlignmentAtom)
		healingItem.applyBrushMask = store.get(healingApplyBrushMaskAtom)
		const generated = await generateAiHealingResult(healingItem, isRegenerate)
		if (!generated) {
			return
		}

		const didConsume = await consumeGenAiCreditAndCancelIfNeeded()
		if (!didConsume) {
			return
		}

		// Cancellation/supersede only skips applying UI result.
		// Credit is still consumed if backend generation already succeeded.
		if (runId !== healingAiGenerationId) {
			return
		}

		await applyAiHealingResult({
			targetPhotoId: generated.targetPhotoId,
			baseHealingItem: healingItem,
			aiResults: generated.aiResults,
			isRegenerate,
			healingMasks: generated.healingMasks,
		})
	} catch (error) {
		if (runId === healingAiGenerationId) {
			console.error(`Failed to ${isRegenerate ? "regenerate" : "generate"} AI healing`, error)
			showToast(getHealingAiErrorMessage(error))
		}
	} finally {
		// Only clean up if this run is still the current one (not cancelled/superseded)
		if (runId === healingAiGenerationId) {
			store.set(healingAiPendingAtom, false)
			store.set(healingAiPendingItemWithPhotoAtom, null)
		}
	}
}

/** Called when user clicks Cancel in the AI progress modal: bump generation ID so the in-flight run is ignored. */
export const cancelHealingAiGeneration = () => {
	healingAiGenerationId++
	store.set(healingAiPendingAtom, false)
	store.set(healingAiPendingItemWithPhotoAtom, null)
}

export const clearAiHealingCandidate = () => {
	const editor = getEditor()
	store.set(healingAiTemporaryItemAtom, null)
	store.set(isHealingHoverAtom, false)
	if (editor.healingCurrentStroke) {
		editor.healingCurrentStroke = null
		// Trigger re-render to clear the red overlay
		editor.render({ reason: "clear-healing-stroke", isDownsampled: true })
	}
}

const captureHealingRegion = (
	editor: ReturnType<typeof getEditor>,
	rect: Rectangle,
	destinationRect: Rectangle,
	aiInputNormalization: HealingAiInputNormalization,
	replaceHealingId?: string,
): CapturedRegion | null => {
	const { renderer } = editor
	const { photo } = renderer
	const initialOrientation = photo.initialOrientation
	const [uncroppedW, uncroppedH] = photo.imageState.originalImageSize

	// Calculate original capture dimensions
	const originalWidth = Math.max(1, Math.round(rect.width * uncroppedW))
	const originalHeight = Math.max(1, Math.round(rect.height * uncroppedH))
	if (originalWidth === 0 || originalHeight === 0) {
		return null
	}

	// Pre-calculate crop information relative to the capture region
	const cropOffsetX = Math.max(
		0,
		Math.round(((destinationRect.x - rect.x) / rect.width) * originalWidth),
	)
	const cropOffsetY = Math.max(
		0,
		Math.round(((destinationRect.y - rect.y) / rect.height) * originalHeight),
	)
	const cropWidth = Math.max(1, Math.round((destinationRect.width / rect.width) * originalWidth))
	const cropHeight = Math.max(
		1,
		Math.round((destinationRect.height / rect.height) * originalHeight),
	)

	// Intelligent resizing for large captures (max 1440px on either dimension)
	const MAX_AI_DIMENSION = 1440
	const maxDimension = Math.max(originalWidth, originalHeight)
	let scaleFactor = 1.0
	let finalWidth = originalWidth
	let finalHeight = originalHeight

	if (maxDimension > MAX_AI_DIMENSION) {
		scaleFactor = MAX_AI_DIMENSION / maxDimension
		finalWidth = Math.round(originalWidth * scaleFactor)
		finalHeight = Math.round(originalHeight * scaleFactor)
		console.log(
			`Resizing large capture from ${originalWidth}x${originalHeight} to ${finalWidth}x${finalHeight} (scale: ${scaleFactor.toFixed(
				3,
			)})`,
		)
	}

	const pixels = renderer.healingPass.renderAiInput(
		rect,
		[finalWidth, finalHeight],
		replaceHealingId,
		aiInputNormalization,
	)

	const imageData = texturePixelsToImageData(pixels, finalWidth, finalHeight)

	// Calculate final orientation and apply it for AI input
	const finalOrientation = initialOrientation + photo.adjustments.orientation
	const orientedImageData = applyOrientation(imageData, finalOrientation)

	// Use JPEG 0.99 for input - nearly lossless but much smaller than PNG
	const base64 = imageDataToBase64(orientedImageData, "jpeg", 0.99)

	// DEBUG: Download input to AI for comparison
	downloadDebugImage(orientedImageData, "ai-input-oriented")

	return {
		base64,
		width: orientedImageData.width, // Use oriented dimensions
		height: orientedImageData.height, // Use oriented dimensions
		unorientedWidth: finalWidth, // Un-oriented dimensions (before rotation)
		unorientedHeight: finalHeight, // Un-oriented dimensions (before rotation)
		unorientedImageData: imageData, // Un-oriented image data for efficient processing
		cropOffsetX: Math.round(cropOffsetX * scaleFactor), // offset in the scaled unoriented capture
		cropOffsetY: Math.round(cropOffsetY * scaleFactor),
		cropWidth: Math.round(cropWidth * scaleFactor), // crop width in the scaled unoriented capture
		cropHeight: Math.round(cropHeight * scaleFactor),
		scaleFactor,
		originalWidth, // original capture size before any scaling
		originalHeight,
		finalOrientation,
		aiInputNormalization,
	}
}

const processAiResult = async (options: {
	aiBlob: Blob
	capture: CapturedRegion
}): Promise<AiHealingResult> => {
	const { aiBlob, capture } = options

	const aiBitmap = await createImageBitmap(aiBlob)

	// Compute edge mismatch alignment using optical flow (in oriented space)
	const inputBitmap = await createImageBitmap(base64ToBlob(capture.base64, "image/jpeg"))
	const opticalFlowResult = await AI.computeOpticalFlowFromBitmaps(aiBitmap, inputBitmap)
	const alignment = computeAlignmentFromFlowRobust(opticalFlowResult)
	inputBitmap.close()

	// Apply inverse orientation to convert AI result to un-oriented space
	let fullBitmap = aiBitmap
	if (capture.finalOrientation !== 0) {
		fullBitmap = await rotateBitmap(aiBitmap, -capture.finalOrientation)
		aiBitmap.close()
	}
	// fullBitmap = resizeImageBitmapToBitmap(
	// 	fullBitmap,
	// 	capture.unorientedWidth,
	// 	capture.unorientedHeight,
	// )

	// Use 0.95 JPEG quality for final output storage
	const fullBlob = await bitmapToBlob(fullBitmap, "image/jpeg", 0.95)
	const fullBase64 = await blobToBase64(fullBlob)

	// Calculate crop rectangle in un-oriented space (normalized coordinates)
	const { cropOffsetX, cropOffsetY, cropWidth, cropHeight } = capture
	const cropRect: Rectangle = {
		x: cropOffsetX / capture.unorientedWidth,
		y: cropOffsetY / capture.unorientedHeight,
		width: cropWidth / capture.unorientedWidth,
		height: cropHeight / capture.unorientedHeight,
	}

	// DEBUG: Download bitmap to inspect orientation
	downloadDebugImage(bitmapToImageData(fullBitmap), "ai-generated-full-unoriented")

	return {
		fullBitmap,
		fullBase64,
		cropRect,
		alignment,
		orientation: capture.finalOrientation,
		enhancedPrompt: null,
		aiInputNormalization: capture.aiInputNormalization,
	}
}

export const updateHealingStatus = (map: Map<string, Rectangle>) => {
	map.forEach((value, key) => {
		updateHealingItem(key, { healingProps: { sourceRect: value } })
	})
}

export const updateHealingItem = (
	healingItemId: string,
	properties: {
		healingProps?: Partial<HealingItem>
		strokeProps?: Partial<BrushStroke>
		changedRegion?: string
	},

	saveState?: boolean,
) => {
	const editor = getEditor()
	const healingMasks = editor.adjustments.healingMasks

	if (healingMasks) {
		const newHealings = healingMasks.map((_healingItem) => {
			if (_healingItem.id === healingItemId) {
				if (properties.strokeProps) {
					const currentStroke = _healingItem.strokes[0]
					if (currentStroke) {
						const newStroke = { ...currentStroke, ...properties.strokeProps }
						return { ..._healingItem, strokes: [newStroke] }
					}
				}
				return { ..._healingItem, ...properties.healingProps }
			}
			return _healingItem
		})

		editor.setAdjustments({ healingMasks: newHealings })
	}

	if (saveState) {
		saveCurrentAdjustmentState({
			message: `Remove`,
			changedValue: "Update " + (properties.changedRegion ?? "source"),
		})
	}
}

export const removeHealingItem = (healingItem: HealingItem) => {
	const editor = getEditor()
	const currentMasks = editor.adjustments.healingMasks ?? []
	const healingMasks = currentMasks.filter((h) => h.id !== healingItem.id)
	store.set(selectedHealingAtom, null)
	editor.setAdjustments({ healingMasks })
	saveCurrentAdjustmentState({
		message: `Remove`,
		changedValue: "Delete Removal",
	})
}

export const resetAllHealings = () => {
	const editor = getEditor()
	store.set(healingAiTemporaryItemAtom, null)
	editor.setAdjustments({ healingMasks: [] })
	saveCurrentAdjustmentState({
		message: `Reset Removals`,
	})
}
